import { Component, input, output, computed, inject } from "@angular/core";
import { PaginationUtil } from "../../util/PaginationUtil";
import { InputSelectUifComponent, SelectOption } from "../input-select-uif/input-select-uif.component";
import { TranslocoDirective, TranslocoService } from "@jsverse/transloco";


@Component({
  selector: "app-pagination-uif",
  imports: [InputSelectUifComponent, TranslocoDirective],
  templateUrl: "./pagination-uif.component.html",
})
export class PaginationUifComponent {
  //#region I/O
  totalRows = input<number>();
  page = input<number>();
  pageChange = output<number>();
  pageSize = input<number>();
  pageSizeChange = output<number>();

  paginationChange = output<Pagination>();
  //#endregion
  //#region Fields
  translocoService = inject(TranslocoService);
  defaultPageSize = PaginationUtil.getDefaultPageSize();
  pageSizeColumnOptions: SelectOption[];
  //#endregion

  constructor() {
    this.pageSizeColumnOptions = [1, 5, 10, 20, 50, 100].map((size) => ({
      label: size.toString(),
      value: size.toString(),
    }));
  }

  //#region UI Events
  emitPaginationChange(newPage: number, newPageSize: number) {
    this.pageChange.emit(newPage);
    this.pageSizeChange.emit(newPageSize);

    this.paginationChange.emit({
      page: newPage,
      pageSize: newPageSize,
      orderBy: "",
      filter: "",
    });
  }

  totalPages = computed((): number => {
    const total = this.totalRows() ?? 0;
    const size = this.pageSize() ?? this.defaultPageSize;
    const pages = Math.ceil(total / size);
    return pages > 0 ? pages : 1;
  });

  onPageSizeChange(newPageSize: string | null | undefined) {
    const size = parseInt(newPageSize ?? "", 10);
    const pageSizeNumber = isNaN(size) ? this.defaultPageSize : size;

    this.emitPaginationChange(1, pageSizeNumber); // Reset to page 1 on change
  }

  pageNumbers = computed(() => {
    const totalPages = this.totalPages();
    const currentPage = this.page() ?? 1;

    if (totalPages <= 7) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }

    const result: (number | string)[] = [];

    result.push(1);

    if (currentPage <= 3) {
      result.push(2, 3, 4, "...", totalPages);
    } else if (currentPage >= totalPages - 2) {
      result.push("...", totalPages - 3, totalPages - 2, totalPages - 1, totalPages);
    } else {
      result.push("...", currentPage - 1, currentPage, currentPage + 1, "...", totalPages);
    }

    return result;
  });

  getPageSizeColumnsOptions(): SelectOption[] {
    return [1, 5, 10, 20, 50, 100].map((size) => ({
      label: size.toString(),
      value: size.toString(),
    }));
  }
}

export interface Pagination {
  page: number;
  pageSize: number;
  orderBy: string;
  filter: string;
}
